import {
  useEffect,
  useRef,
  useState,
  type ComponentProps,
  type MouseEvent,
} from "react"
import type { Row } from "@tanstack/react-table"
import cn from "@/v2-ui/utils/utils.cn"
import TableBodyCell from "./TableBodyCell"

type TableBodyRowMouseEvent = MouseEvent<
  HTMLTableRowElement,
  globalThis.MouseEvent
>

export type TableBodyRowProps<TData = unknown> = Omit<
  ComponentProps<"tr">,
  "onMouseEnter" | "onMouseLeave"
> & {
  row: Row<TData>
  isSelected: boolean
  isActive: boolean
  isClickable: boolean
  onRowClick(row: Row<TData>, e: TableBodyRowMouseEvent): void
  /** Windowed rendering measures real row heights through this (the row
   *  keeps its own internal ref for hover bookkeeping, so the two refs
   *  are composed rather than replaced). */
  measureRef?(el: HTMLTableRowElement | null): void
}

const DATA_STATE_OPEN_SELECTOR = '[data-state="open"]'

function TableBodyRow<TData = unknown>(props: TableBodyRowProps<TData>) {
  const {
    row,
    isSelected,
    isActive,
    isClickable,
    onRowClick,
    measureRef,
    className,
    ...trProps
  } = props

  const trRef = useRef<HTMLTableRowElement>(null)
  const [isHovered, setIsHovered] = useState(false)

  useEffect(() => {
    const tr = trRef.current
    if (!tr) return

    const observer = new MutationObserver(() => {
      const hasOpenMenu = tr.querySelector(DATA_STATE_OPEN_SELECTOR) !== null
      if (!hasOpenMenu && !tr.matches(":hover")) {
        setIsHovered(false)
      }
    })

    observer.observe(tr, {
      subtree: true,
      attributes: true,
      attributeFilter: ["data-state"],
    })

    return () => observer.disconnect()
  }, [])

  return (
    <tr
      ref={(el) => {
        trRef.current = el
        measureRef?.(el)
      }}
      className={cn(
        "ui-table-body-row",
        {
          "ui-table-body-row-is-selected": isSelected,
          "ui-table-body-row-is-active": isActive,
          "ui-table-body-row-is-clickable": isClickable,
        },
        className,
      )}
      data-state={isSelected ? "selected" : undefined}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        if (trRef.current?.querySelector(DATA_STATE_OPEN_SELECTOR)) return
        setIsHovered(false)
      }}
      onClick={isClickable ? (e) => onRowClick(row, e) : undefined}
      {...trProps}
    >
      {row.getVisibleCells().map((cell) => (
        <TableBodyCell<TData> key={cell.id} cell={cell} isHovered={isHovered} />
      ))}
    </tr>
  )
}

export default TableBodyRow
